import React from 'react'
import PropTypes from 'prop-types'
import { CsvDownloadLink } from 'secondstep-components'
import RosteredLicenseInfo from 'components/RosteredLicenseInfo'
import { isTrainingReportAvailable, isLessonReportAvailable } from 'lib/util'

import {
  ASSIGN_LICENSES_BUTTON_TEXT,
  RENEW_LICENSES_BUTTON_TEXT,
  TRAINING_DOWNLOAD_TEXT,
  LESSON_DOWNLOAD_LINK,
  EXPIRED_LICENSES_MESSAGE,
  SELA_REPORTS_LINK,
  BASIC_LESSON_REPORT_TEXT,
  LICENSE_CONTAINER_HEADER_TEXT,
  LICENSE_HEADER_TEXT,
  EXPIRATION_DATE_HEADER_TEXT,
  PROGRAM_REPORTS_HEADER_TEXT,
  SITE_SUMMARY_EXPIRED_MESSAGE,
  RENEW_FAQ_LINK,
} from './constants'
import {
  AssignLicensesButton,
  Expiration,
  Icon,
  LicenseName,
  LicenseRow,
  SiteName,
  SiteSummaryContainer,
  ReportLink,
  ReportLinkWrapper,
  TableHeader,
  ColumnHeader,
  RenewLicensesButton,
  SiteSummaryTable,
  StyledGraphIcon,
} from './component.styles'

import Warning from 'components/GlobalNav/assets/warning.svg'

const formatExpirationDate = dateString =>
  new Date(dateString).toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

const getLessonReportLink = asset =>
  asset.product.programCodes.some(code => code === 'ADL')
    ? SELA_REPORTS_LINK
    : LESSON_DOWNLOAD_LINK

const renderLicenseRow = (
  asset,
  bpuTrainingDownloadData,
  cpuTrainingDownloadData,
) => {
  const {
    showTrainingReportDownload,
    trainingData: { data, filename, headers } = {},
  } = isTrainingReportAvailable(
    asset,
    bpuTrainingDownloadData,
    cpuTrainingDownloadData,
  )
  const showLessonReportLink = isLessonReportAvailable(asset)
  const formattedDate = formatExpirationDate(asset.expirationDate)
  const isActive =
    asset.isActive !== undefined ? asset.isActive : asset.status !== 'Expired'

  return (
    <LicenseRow key={asset.id}>
      <LicenseName>
        {`${asset?.product?.description}, ID: ${asset?.nsName}`}
      </LicenseName>
      <Expiration>
        {`Expire${isActive ? 's' : 'd'} ${formattedDate}`}
      </Expiration>
      {showTrainingReportDownload && data?.length > 0 && (
        <ReportLinkWrapper>
          <CsvDownloadLink
            data={data}
            data-testid="training-download-link"
            filename={filename}
            headers={headers}
            isAsync={false}
          >
            {TRAINING_DOWNLOAD_TEXT}
          </CsvDownloadLink>
        </ReportLinkWrapper>
      )}
      {showLessonReportLink && (
        <ReportLinkWrapper>
          <StyledGraphIcon />
          <ReportLink
            dataTestId="download-report-link"
            href={getLessonReportLink(asset)}
          >
            {BASIC_LESSON_REPORT_TEXT}
          </ReportLink>
        </ReportLinkWrapper>
      )}
    </LicenseRow>
  )
}

const renderExpiredLicenses = (
  hasUnassignedLicenses,
  redirectToLicenseManagement,
) => (
  <>
    <span>
      <Icon alt="Warning" label="WARNING" src={Warning} />
      Second Step<sup>® </sup>
      {SITE_SUMMARY_EXPIRED_MESSAGE}
    </span>
    <br />
    <span>{EXPIRED_LICENSES_MESSAGE}</span>
    <br />
    <RenewLicensesButton onClick={() => window.open(RENEW_FAQ_LINK)}>
      {RENEW_LICENSES_BUTTON_TEXT}
    </RenewLicensesButton>
    {hasUnassignedLicenses && (
      <AssignLicensesButton onClick={redirectToLicenseManagement}>
        {ASSIGN_LICENSES_BUTTON_TEXT}
      </AssignLicensesButton>
    )}
  </>
)

const renderLicenseTable = (
  assets,
  bpuTrainingDownloadData,
  cpuTrainingDownloadData,
) => (
  <>
    <TableHeader>{LICENSE_CONTAINER_HEADER_TEXT}</TableHeader>
    <SiteSummaryTable>
      <thead>
        <tr>
          <ColumnHeader>{LICENSE_HEADER_TEXT}</ColumnHeader>
          <ColumnHeader>{EXPIRATION_DATE_HEADER_TEXT}</ColumnHeader>
          <ColumnHeader>{PROGRAM_REPORTS_HEADER_TEXT}</ColumnHeader>
        </tr>
      </thead>
      <tbody>
        {assets
          .filter(asset => {
            const isActive =
              asset.isActive !== undefined ? asset.isActive : !asset.isExpired
            return isActive && !asset.cancelledDate
          })
          .map(asset =>
            renderLicenseRow(
              asset,
              bpuTrainingDownloadData,
              cpuTrainingDownloadData,
            ),
          )}
      </tbody>
    </SiteSummaryTable>
  </>
)

const SiteSummary = ({
  bpuTrainingDownloadData,
  cpuTrainingDownloadData,
  hasUnassignedLicenses,
  redirectToLicenseManagement,
  selectedSite,
}) => {
  if (!selectedSite) return null

  const hasActiveAssets = selectedSite.netSuiteAssets.some(asset =>
    asset.isActive !== undefined ? asset.isActive : !asset.status !== 'Expired',
  )

  return (
    <>
      <SiteName>{selectedSite.name}</SiteName>
      <SiteSummaryContainer>
        {hasActiveAssets
          ? renderLicenseTable(
              selectedSite.netSuiteAssets,
              bpuTrainingDownloadData,
              cpuTrainingDownloadData,
            )
          : renderExpiredLicenses(
              hasUnassignedLicenses,
              redirectToLicenseManagement,
            )}
      </SiteSummaryContainer>
      {selectedSite.isRostered && <RosteredLicenseInfo />}
    </>
  )
}

SiteSummary.propTypes = {
  bpuTrainingDownloadData: PropTypes.object,
  cpuTrainingDownloadData: PropTypes.object,
  hasUnassignedLicenses: PropTypes.bool,
  redirectToLicenseManagement: PropTypes.func,
  selectedSite: PropTypes.object,
}

export default SiteSummary
